import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Icon } from '../ui/Icon';
import { GiaLogo } from '../ai/GiaLogo';
import Spinner from '../ui/Spinner';
import AiHintPopover from './AiHintPopover';
import type { DayRouteGroup } from '../../utils/routeGroups';

interface Props {
  group: DayRouteGroup;
  destination: string;
  optimizing: boolean;
  /** Absent = read-only: the gIA optimiser writes to the itinerary. */
  onOptimize?: (hint?: string) => void;
}

/**
 * The actions that used to sit at the foot of the single-day panel, now in each
 * day's header — with several days on screen there is no "the" day for a footer
 * button to act on.
 */
export default function DayRouteActions({
  group, destination, optimizing, onOptimize,
}: Props) {
  const { t } = useTranslation();
  const [hintOpen, setHintOpen] = useState(false);
  const aiButtonRef = useRef<HTMLButtonElement | null>(null);

  const stops = group.stops.map(s => s.activity);
  const mapsUrl = stops.length >= 2
    ? `https://www.google.com/maps/dir/${stops.map(a =>
        encodeURIComponent(a.address ?? a.venueName ?? `${a.title}, ${destination}`)
      ).join('/')}`
    : null;

  return (
    <div className="flex items-center gap-1 shrink-0">
      {onOptimize && (
      <button
        ref={aiButtonRef}
        onClick={() => setHintOpen(o => !o)}
        disabled={optimizing || stops.length < 2}
        title={`${t('map.optimizeWith')} gIA`}
        aria-label={`${t('map.optimizeWith')} gIA`}
        className="w-7 h-7 rounded-lg flex items-center justify-center text-violet-600 hover:bg-violet-50 dark:text-violet-300 dark:hover:bg-violet-900/30 transition-colors disabled:opacity-30 disabled:cursor-default cursor-pointer"
      >
        {optimizing ? <Spinner size="xs" /> : <GiaLogo size={15} />}
      </button>
      )}

      {mapsUrl && (
        <a
          href={mapsUrl} target="_blank" rel="noopener noreferrer"
          title={t('map.openInMaps')} aria-label={t('map.openInMaps')}
          className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-teal-600 hover:bg-teal-50 dark:hover:text-teal-400 dark:hover:bg-teal-900/30 transition-colors"
        >
          <Icon as={ExternalLink} size={14} />
        </a>
      )}

      {onOptimize && hintOpen && !optimizing && (
        <AiHintPopover
          anchorRef={aiButtonRef}
          onClose={() => setHintOpen(false)}
          onSubmit={hint => { setHintOpen(false); onOptimize(hint); }}
        />
      )}
    </div>
  );
}
